/**
 * "Back into your offline rave" — on the no-signal screen, for the account that signed out on this address
 * (the QA walk, w-offline-per-account; the rule and its safety in `lib/offlineReentry.ts`). The person types the
 * account's email; a match signs this device in as that account again, offline, and the page reloads onto the
 * account's own stored rave. Nothing is drawn when there is no such account.
 */
import { useId, useState, type FormEvent } from 'react';
import { Button, Field, Stack } from '@/ui';
import { useT } from '@/i18n';
import { reenterOffline, reentryCandidate } from '@/lib/offlineReentry';

export function OfflineReentry({ onBack = () => window.location.reload() }: { onBack?: () => void }) {
  const tr = useT();
  const id = useId();
  const [who] = useState(() => reentryCandidate());
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState(false);
  if (!who) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setWrong(false);
    const ok = await reenterOffline(email);
    setBusy(false);
    if (ok) onBack();
    else setWrong(true);
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="offline-reentry">
      <Stack gap={8}>
        <strong>{who.name
          ? tr('Signed out with no signal, {name}?', { name: who.name })
          : tr('Signed out with no signal?')}</strong>
        <span>{tr('Type your account’s email to get back into your offline rave on this phone. You will sign in properly once there is a signal.')}</span>
        <label htmlFor={id} className="ui-sr-only">{tr('Your account’s email')}</label>
        <Field id={id} type="email" autoComplete="email" value={email} onChange={setEmail} placeholder={tr('Your account’s email')} />
        {wrong && <span role="alert" className="ui-err">{tr('That is not this account’s email.')}</span>}
        <Button type="submit" loading={busy} disabled={!email.trim()}>{tr('Back into my offline rave')}</Button>
      </Stack>
    </form>
  );
}
